// Stealth-Agent FE — Org Intelligence. AI-generated org report (Claude over stealth
// data) + KPIs, app distribution, leaderboard, semantic search. Premium SaaS UI with
// framer-motion micro-interactions. Same data + endpoints + logic as before — only the
// presentation layer is elevated. ISOLATED: imports nothing from TG's own pages.
import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Button, Empty } from 'antd';
import toast from 'react-hot-toast';
import {
  Monitor, Users, Coffee,
  RefreshCw, Brain, Zap, Activity, TrendingUp, Sparkles, Download, Gauge, Medal, Flame, Star, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { getDevices, getReports, ORG_INTEL_STREAM } from '../api/client';
import { useStealthAiStream, mdToHtml } from './useStealthAiStream';
import { fmtDuration, relativeTime, isOnline, deviceDisplayName } from './utils';
import {
  Card, Kpi, PageHeader, SectionTitle, Reveal, RevealGroup,
  Skeleton, CHART_PALETTE, EASE, downloadReportPDF,
} from './ui';

// Custom recharts tooltip — clean, on-brand.
function ChartTip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-xl">
      <div className="font-medium">{p.name}</div>
      <div className="text-gray-300">{fmtDuration(p.value)}</div>
    </div>
  );
}

export default function OrgIntelligence() {
  const MAX_RANGE_DAYS = 7;
  const [devices, setDevices] = useState([]);
  const [reports, setReports] = useState([]);
  const [overall, setOverall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState([dayjs(), dayjs()]);
  const ai = useStealthAiStream(ORG_INTEL_STREAM);
  const VIEWER_TZ = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'; } catch { return 'Asia/Kolkata'; }
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        getDevices(),
        getReports({
          limit: 100,
          totals: 1,
          startDate: range?.[0]?.format('YYYY-MM-DD'),
          endDate: range?.[1]?.format('YYYY-MM-DD'),
          tz: VIEWER_TZ,
        }),
      ]);
      setDevices(d.devices || []);
      setReports(r.reports || []);
      setOverall(r.totals || null);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [range, VIEWER_TZ]);

  const stats = useMemo(() => {
    const online = devices.filter((x) => isOnline(x)).length;
    const nameByUser = {};
    const avatarByUser = {};
    devices.forEach((d) => {
      if (d.user_id) {
        nameByUser[d.user_id] = deviceDisplayName(d);
        const av = d.avatar || d.user_avatar;
        if (av) avatarByUser[d.user_id] = av;
      }
    });
    let active = 0, idle = 0, keys = 0, clicks = 0, copies = 0, pastes = 0;
    const appTime = {}; const byUser = {};
    reports.forEach((r) => {
      active += r.time_active_sec || 0; idle += r.time_idle_sec || 0;
      keys += r.keyboard_key_presses || 0; clicks += r.mouse_clicks || 0;
      copies += r.clipboard_copies || 0; pastes += r.clipboard_pastes || 0;
      if (r.active_app) appTime[r.active_app] = (appTime[r.active_app] || 0) + (r.time_active_sec || 0) + 1;
      const u = r.user_id || 'unknown';
      byUser[u] = byUser[u] || { user_id: u, reports: 0, keys: 0, clicks: 0, apps: {} };
      byUser[u].reports += 1; byUser[u].keys += r.keyboard_key_presses || 0; byUser[u].clicks += r.mouse_clicks || 0;
      if (r.active_app) byUser[u].apps[r.active_app] = (byUser[u].apps[r.active_app] || 0) + 1;
    });
    const worked = active + idle; // fallback from sampled reports
    const focusPct = worked ? Math.round((active / worked) * 100) : 0;
    const topApps = Object.entries(appTime).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, v]) => ({ name, value: Math.round(v) }));
    const leaderboard = Object.values(byUser).map((u) => ({
      ...u,
      name: nameByUser[u.user_id] || 'Team member',
      avatar: avatarByUser[u.user_id] || null,
      topApp: Object.entries(u.apps).sort((a, b) => b[1] - a[1])[0]?.[0] || '—',
    })).sort((a, b) => b.reports - a.reports);
    const maxReports = Math.max(1, ...leaderboard.map((u) => u.reports));
    const finalActive = overall?.active ?? active;
    const finalIdle = overall?.idle ?? idle;
    const finalWorked = overall?.worked ?? worked;
    const finalFocusPct = overall?.active_pct ?? focusPct;
    const finalKeys = overall?.keys ?? keys;
    const finalClicks = overall?.clicks ?? clicks;
    return {
      online,
      active: finalActive,
      idle: finalIdle,
      worked: finalWorked,
      focusPct: finalFocusPct,
      keys: finalKeys,
      clicks: finalClicks,
      copies,
      pastes,
      topApps,
      leaderboard,
      maxReports,
      reportsAnalyzed: overall?.reports ?? reports.length,
    };
  }, [devices, reports, overall]);

  const generate = () => ai.run({
    startDate: range?.[0]?.format('YYYY-MM-DD'),
    endDate: range?.[1]?.format('YYYY-MM-DD'),
  });
  const handleRangeChange = (nextRange) => {
    if (!nextRange || !nextRange[0] || !nextRange[1]) {
      setRange([dayjs(), dayjs()]);
      return;
    }
    const ordered = nextRange[0].isBefore(nextRange[1], 'day')
      ? [nextRange[0], nextRange[1]]
      : [nextRange[1], nextRange[0]];
    const spanDays = ordered[1].diff(ordered[0], 'day') + 1;
    if (spanDays > MAX_RANGE_DAYS) {
      const clampedEnd = ordered[0].add(MAX_RANGE_DAYS - 1, 'day');
      setRange([ordered[0], clampedEnd]);
      toast.error('Maximum 7 days range allowed');
      return;
    }
    setRange(ordered);
  };
  const disableBeyondSevenDays = (current, info) => {
    if (!current) return false;
    if (current.isAfter(dayjs().endOf('day'), 'day')) return true;
    if (!info?.from) return false;
    const from = dayjs(info.from);
    return current.isAfter(from.add(MAX_RANGE_DAYS - 1, 'day'), 'day')
      || current.isBefore(from.subtract(MAX_RANGE_DAYS - 1, 'day'), 'day');
  };
  const momentumScore = Math.max(0, Math.min(100, Math.round((stats.focusPct * 0.75) + (Math.min(stats.reportsAnalyzed || 0, 300) / 300) * 25)));
  const consistencyScore = Math.max(0, Math.min(100, Math.round((stats.leaderboard.filter((u) => u.reports > 0).length / Math.max(1, stats.leaderboard.length)) * 100)));
  const rankTier = momentumScore >= 85 ? 'Platinum' : momentumScore >= 70 ? 'Gold' : momentumScore >= 55 ? 'Silver' : 'Bronze';
  const achievementChips = useMemo(() => {
    const chips = [];
    if (stats.focusPct >= 75) chips.push({ label: 'Org Focus', icon: Flame });
    if ((stats.keys || 0) >= 10000) chips.push({ label: 'High Throughput', icon: Star });
    if (consistencyScore >= 70) chips.push({ label: 'Steady Team', icon: Medal });
    if (chips.length === 0) chips.push({ label: 'Momentum Building', icon: Star });
    return chips;
  }, [stats.focusPct, stats.keys, consistencyScore]);
  const aiGamified = useMemo(() => {
    if (!ai.text) return { highlights: [], wins: 0, watchouts: 0 };
    const lines = String(ai.text)
      .replace(/\r/g, '')
      .split('\n')
      .map((l) => l.replace(/\*\*/g, '').trim())
      .filter(Boolean);
    const bullets = lines
      .filter((l) => /^[-*•]\s+/.test(l))
      .map((l) => l.replace(/^[-*•]\s+/, '').trim())
      .filter((l) => l.length > 12);
    const fallback = lines
      .filter((l) => !/^section\s*\d+/i.test(l) && l.length > 30 && !/^\d+[.)]/.test(l))
      .slice(0, 4);
    const highlights = (bullets.length ? bullets : fallback).slice(0, 4);
    const positiveRegex = /(completed|implemented|resolved|improved|delivered|optimized|success|strong|effective)/i;
    const riskRegex = /(issue|risk|blocked|delay|pending|bottleneck|error|concern)/i;
    const wins = highlights.filter((h) => positiveRegex.test(h)).length;
    const watchouts = highlights.filter((h) => riskRegex.test(h)).length;
    return { highlights, wins, watchouts };
  }, [ai.text]);

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px] mx-auto">
      <PageHeader
        icon={Brain}
        title="Org Intelligence"
        subtitle="AI workforce intelligence synthesized from live work activity."
        badge="Stealth"
        actions={
          <>
            <DatePicker.RangePicker
              value={range}
              onChange={handleRangeChange}
              disabledDate={disableBeyondSevenDays}
            />
            <Button type="primary" icon={<Zap size={15} />} loading={ai.streaming} onClick={generate}
              style={{ background: 'linear-gradient(135deg,#6366f1,#7c3aed)', border: 'none', fontWeight: 500 }}>
              Generate report
            </Button>
            <Button icon={<RefreshCw size={14} />} onClick={load} loading={loading}>Refresh</Button>
          </>
        }
      />

      {/* KPIs */}
      <RevealGroup className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Monitor} label="Devices online" accent="emerald"
          value={<span><span className="tabular-nums">{stats.online}</span><span className="text-gray-300 dark:text-gray-600 text-xl">/{devices.length}</span></span>} />
        <Kpi icon={Activity} label="Worked (total)" accent="indigo" value={fmtDuration(stats.worked)} sub={`${stats.focusPct}% active`} />
        <Kpi icon={Coffee} label="Idle time" accent="amber" value={fmtDuration(stats.idle)} sub={`${100 - stats.focusPct}% of worked`} />
        <Kpi icon={Activity} label="Input activity" accent="sky"
          value={<span className="text-[20px]"><span className="tabular-nums">{(stats.keys || 0).toLocaleString()}</span> <span className="text-gray-400 text-sm font-normal">keys</span></span>}
          sub={`${(stats.clicks || 0).toLocaleString()} pointer · ${stats.copies + stats.pastes} copy/paste`} />
      </RevealGroup>

      {/* AI report — aligned with Work Summary layout */}
      <Reveal>
        <Card className="p-6 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gradient-to-br from-indigo-500/10 to-violet-500/10 blur-3xl pointer-events-none" />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <SectionTitle icon={Sparkles} right={
                ai.text && !ai.streaming ? (
                  <Button size="small" icon={<Download size={13} />} onClick={() => downloadReportPDF({
                    title: 'Org Intelligence Report',
                    subtitle: `${range?.[0]?.format('MMM D')} – ${range?.[1]?.format('MMM D, YYYY')}`,
                    bodyHtml: mdToHtml(ai.text),
                    meta: { Range: `${range?.[0]?.format('YYYY-MM-DD')} to ${range?.[1]?.format('YYYY-MM-DD')}`, People: stats.leaderboard.length, Worked: fmtDuration(stats.worked), 'Active focus': `${stats.focusPct}%` },
                  })}>Download PDF</Button>
                ) : null
              }>
                <span className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">AI Org Report</span>
              </SectionTitle>
              <div className="xl:h-[540px]">
                <AnimatePresence mode="wait">
                  {ai.streaming && !ai.text && (
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2.5">
                      <Skeleton className="h-3.5 w-3/4" /><Skeleton className="h-3.5 w-full" /><Skeleton className="h-3.5 w-5/6" /><Skeleton className="h-3.5 w-2/3" />
                      <div className="flex items-center gap-2 text-xs text-indigo-500 pt-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" /> Analyzing workforce signals…
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {ai.error && <div className="text-sm text-amber-600">{ai.error}</div>}
                {!ai.text && !ai.streaming && !ai.error && (
                  <div className="h-full min-h-[260px] rounded-2xl border border-dashed border-gray-200 dark:border-white/[0.12]
                    bg-gray-50/70 dark:bg-white/[0.02] flex flex-col items-center justify-center text-center px-6">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/15 to-violet-500/10 flex items-center justify-center mb-3">
                      <Sparkles size={20} className="text-indigo-500" />
                    </div>
                    <div className="text-[15px] font-semibold text-gray-800 dark:text-gray-100">AI Org Report is ready to generate</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                      Choose a date range and click <span className="font-medium text-indigo-600 dark:text-indigo-300">Generate report</span> to get organization-wide highlights, wins, and risks.
                    </div>
                  </div>
                )}
                {ai.text && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                    className="h-full overflow-y-auto pr-2 prose-stealth text-[14px] text-gray-700 dark:text-gray-200 leading-[1.7]
                      [&::-webkit-scrollbar]:w-2
                      [&::-webkit-scrollbar-track]:bg-transparent
                      [&::-webkit-scrollbar-thumb]:bg-gray-300
                      dark:[&::-webkit-scrollbar-thumb]:bg-white/20
                      [&::-webkit-scrollbar-thumb]:rounded-full"
                    dangerouslySetInnerHTML={{ __html: mdToHtml(ai.text) }} />
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-500/[0.08] dark:to-violet-500/[0.08]">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">Performance Arena</div>
                <div className="flex items-center gap-4">
                  <div
                    className="w-20 h-20 rounded-full grid place-items-center text-sm font-semibold text-indigo-700 dark:text-indigo-300"
                    style={{ background: `conic-gradient(#4f46e5 ${momentumScore}%, rgba(148,163,184,0.25) 0%)` }}
                  >
                    <span className="w-14 h-14 rounded-full bg-white dark:bg-gray-900 grid place-items-center">{momentumScore}</span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Momentum score</div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">{momentumScore}/100</div>
                    <div className="text-xs text-indigo-700 dark:text-indigo-300">{rankTier} tier performance</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Consistency</span>
                  <span className="tabular-nums">{consistencyScore}%</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-gray-100 dark:bg-white/[0.08] overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${consistencyScore}%` }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-violet-500 to-indigo-500"
                  />
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Reports analyzed: <span className="font-medium text-gray-700 dark:text-gray-200">{(stats.reportsAnalyzed || 0).toLocaleString()}</span>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Achievements</div>
                <div className="flex flex-wrap gap-2">
                  {achievementChips.map((chip) => {
                    const Icon = chip.icon;
                    return (
                      <span key={chip.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-200">
                        <Icon size={12} />
                        {chip.label}
                      </span>
                    );
                  })}
                  {aiGamified.wins > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
                      <CheckCircle2 size={12} />
                      {aiGamified.wins} AI wins
                    </span>
                  )}
                  {aiGamified.watchouts > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                      <AlertTriangle size={12} />
                      {aiGamified.watchouts} watchouts
                    </span>
                  )}
                </div>
              </div>

              {aiGamified.highlights.length > 0 && (
                <div className="rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">AI Missions</div>
                  <div className="space-y-2 max-h-[230px] overflow-y-auto pr-1
                    [&::-webkit-scrollbar]:w-2
                    [&::-webkit-scrollbar-track]:bg-transparent
                    [&::-webkit-scrollbar-thumb]:bg-gray-300
                    dark:[&::-webkit-scrollbar-thumb]:bg-white/20
                    [&::-webkit-scrollbar-thumb]:rounded-full">
                    {aiGamified.highlights.map((item, idx) => (
                      <div key={`${idx}-${item.slice(0, 24)}`} className="rounded-xl border border-gray-100 dark:border-white/[0.06] px-3 py-2 bg-gray-50/70 dark:bg-white/[0.02]">
                        <div className="text-[11px] text-indigo-600 dark:text-indigo-300 font-medium mb-1">Mission {idx + 1}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-2">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </Reveal>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <Reveal className="lg:col-span-2">
          <Card className="p-5 h-full">
            <SectionTitle icon={TrendingUp}>App usage (org-wide)</SectionTitle>
            {loading ? <Skeleton className="h-[240px] w-full" /> : stats.topApps.length === 0 ? <Empty description="No data yet" /> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={stats.topApps} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={56} outerRadius={92}
                    paddingAngle={2} stroke="none" animationDuration={800} animationBegin={150}>
                    {stats.topApps.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
            {!loading && stats.topApps.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                {stats.topApps.slice(0, 6).map((a, i) => (
                  <div key={a.name} className="flex items-center gap-1.5 text-xs text-gray-500">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                    {a.name}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Reveal>

        <Reveal className="lg:col-span-3">
          <Card className="p-5 h-full">
            <SectionTitle icon={Users} right={<span className="text-xs text-gray-400">{stats.leaderboard.length} people</span>}>Employee activity</SectionTitle>
            <div className="overflow-auto max-h-72 -mx-1 px-1">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wide text-gray-400 sticky top-0 bg-white dark:bg-[#0f1115]">
                  <tr className="text-left">
                    <th className="py-2 font-medium">User</th>
                    <th className="font-medium">Activity</th>
                    <th className="text-right font-medium">Keys</th>
                    <th className="text-right font-medium">Clicks</th>
                    <th className="text-left pl-4 font-medium">Top app</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && [...Array(4)].map((_, i) => (
                    <tr key={i}><td colSpan={5} className="py-2"><Skeleton className="h-7 w-full" /></td></tr>
                  ))}
                  {!loading && stats.leaderboard.map((u, i) => (
                    <motion.tr key={u.user_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                      className="border-t border-gray-50 dark:border-white/[0.04] hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                      <td className="py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 text-white text-[10px] font-semibold flex items-center justify-center overflow-hidden">
                            {u.avatar ? (
                              <img src={u.avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              (u.name || u.user_id).slice(0, 2).toUpperCase()
                            )}
                          </div>
                          <span className="text-gray-700 dark:text-gray-200 font-medium">{u.name}</span>
                        </div>
                      </td>
                      <td className="pr-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 max-w-[120px] h-1.5 rounded-full bg-gray-100 dark:bg-white/[0.06] overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${(u.reports / stats.maxReports) * 100}%` }} transition={{ duration: 0.7, ease: EASE, delay: i * 0.03 }}
                              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500" />
                          </div>
                          <span className="text-xs text-gray-400 tabular-nums">{u.reports}</span>
                        </div>
                      </td>
                      <td className="text-right text-gray-600 dark:text-gray-300 tabular-nums">{u.keys.toLocaleString()}</td>
                      <td className="text-right text-gray-600 dark:text-gray-300 tabular-nums">{u.clicks.toLocaleString()}</td>
                      <td className="pl-4 text-gray-500">{u.topApp}</td>
                    </motion.tr>
                  ))}
                  {!loading && stats.leaderboard.length === 0 && <tr><td colSpan="5" className="text-gray-400 py-4">No data yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </Reveal>
      </div>
    </div>
  );
}
